import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalVariant } from '@/features/configuration-management/location/table/Column';
import { CheckBoxIcon, EditIcon, WindowCloseSolidIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { StandardizedApiError } from '@/services/client/types';
import { HoslogRequestPlans, PLAN_REQUEST_STATUS } from '@/services/hoslog-plans/hoslog-plans-request.types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { ReactNode } from 'react';
import HoslogPlanRequestsForm from '../../HoslogPlanRequestsForm';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

export function getColumns(
  onOpen: OnOpenProps,
  mutate: UseMutateAsyncFunction<
    {
      message: string;
      data: {
        clubId: string;
        planRequestId: string;
        status: string;
      };
    },
    StandardizedApiError,
    {
      clubId: string;
      planRequestId: string;
      status: 'A' | 'R' | 'D';
    },
    unknown
  >,
  t: (key: string) => string,
  openConfirmation: (data: {
    title: string;
    description: string;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction: () => void | Promise<void>;
  }) => void,
): ColumnDef<HoslogRequestPlans>[] {
  return [
    {
      accessorKey: 'sno',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="truncate max-w-[10rem] text-caption font-medium">{getSerialNumber(table, row.index)}</div>
      ),
      enableHiding: false,
      enableSorting: false,
    },

    {
      accessorKey: 'storeInfo',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        return (
          <div className="flex items-center space-x-2 max-w-[20rem] truncate">
            <img
              src={row.original.clubImage?.path}
              alt={row.original.clubImage?.alt ?? ''}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className="truncate">
              <div className="font-medium text-xs text-link underline">{row.original.storeNameJp}</div>
              <div className="text-foreground-subtle font-medium text-xs">{row.original.storeNameEng}</div>
            </div>
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.storeInfo',
      },
      enableSorting: false,
    },
    {
      accessorKey: 'planTitle',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.planTitle')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-primary text-caption font-medium line-clamp-2">{row.getValue('planTitle')}</div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.planTitle',
      },
    },

    {
      accessorKey: 'planPrice',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.planPrice')}
        />
      ),
      cell: ({ row }) => (
        <div className="text-right text-primary text-caption font-medium">{row.getValue('planPrice')} 円</div>
      ),
      meta: { align: 'right', hideColumnLabelKey: 'common.page.tableColumn.planPrice' },
    },
    {
      accessorKey: 'nomination',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('configurationManagement.plans.page.managePlansModal.nomination')}
        />
      ),
      cell: ({ row }) => <div className="text-primary text-caption font-medium">{row.getValue('nomination')}</div>,
      meta: {
        hideColumnLabelKey: 'configurationManagement.plans.page.managePlansModal.nomination',
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.status')}
        />
      ),
      cell: ({ row }) => (
        <div>
          {row.getValue('status') === 'A' ? (
            <div className="text-success text-caption font-medium">{t('common.page.table.approved')}</div>
          ) : row.getValue('status') === 'P' ? (
            <div className="text-warning text-caption font-medium">{t('common.page.table.pending')}</div>
          ) : (
            <div className="text-destructive-text text-caption font-medium">{t('common.page.table.rejected')}</div>
          )}
        </div>
      ),
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.status',
        label: t('common.page.tableColumn.status'),
        options: [
          { value: 'A', label: t('common.page.table.approved') },
          { value: 'P', label: t('common.page.table.pending') },
          { value: 'R', label: t('common.page.table.rejected') },
        ],
        statusClassName: 'px-4',
      },
    },
    {
      accessorKey: 'requestedDate',
      id: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.requestedDate')}
        />
      ),
      cell: ({ row }) => {
        const date = row.getValue<Date>('createdDate');
        return (
          <div className="whitespace-nowrap text-primary text-caption font-medium">
            {formatDate(date, 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      meta: {
        label: 'Created Date',
        hideColumnLabelKey: 'common.page.tableColumn.requestedDate',
      },
      enableColumnFilter: true,
    },
    {
      id: 'actions',
      accessorKey: 'Actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.actions')}
        />
      ),
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => {
        const status = row.getValue('status');
        return (
          <div className="flex gap-4 items-center">
            {status === 'P' ? (
              <Can rule={{ allOf: [PERMS.plan.request.list, PERMS.plan.request.approve] }}>
                <CheckBoxIcon
                  className="text-status-success hover:cursor-pointer"
                  height={16}
                  width={16}
                  onClick={() => {
                    openConfirmation({
                      title: t('common.page.modal.confirm'),
                      description: t('configurationManagement.plans.page.statusUpdateModel.approve'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.submit'),
                      size: 'lg',
                      onAction: async () => {
                        await mutate({
                          clubId: row.original.clubId,
                          planRequestId: row.original.planRequestId ?? '',
                          status: PLAN_REQUEST_STATUS.APPROVE,
                        });
                      },
                      variant: 'default',
                    });
                  }}
                />
              </Can>
            ) : status === 'A' ? (
              <Can rule={{ allOf: [PERMS.plan.request.update, PERMS.plan.request.detail] }}>
                <EditIcon
                  className="text-foreground-subtle hover:cursor-pointer"
                  height={16}
                  width={16}
                  onClick={() =>
                    onOpen({
                      modalTitle: 'configurationManagement.plans.page.managePlansModal.hoslogPlansRequest',
                      title: 'Hoslog Plan',
                      okText: t('common.page.button.approve'),
                      cancelText: t('common.page.button.cancel'),
                      size: 'lg',
                      component: (
                        <HoslogPlanRequestsForm
                          clubId={row.original.clubId}
                          planRequestId={row.original.planRequestId}
                          planId={row.original.planId}
                        />
                      ),
                    })
                  }
                />
              </Can>
            ) : null}
            {status == 'P' && (
              <Can rule={{ allOf: [PERMS.plan.request.list, PERMS.plan.request.approve] }}>
                <WindowCloseSolidIcon
                  className="text-status-error hover:cursor-pointer"
                  height={16}
                  width={16}
                  onClick={() => {
                    openConfirmation({
                      title: t('common.page.modal.confirm'),
                      description: t('configurationManagement.plans.page.statusUpdateModel.reject'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.submit'),
                      size: 'lg',
                      onAction: async () => {
                        await mutate({
                          clubId: row.original.clubId,
                          planRequestId: row.original.planRequestId ?? '',
                          status: PLAN_REQUEST_STATUS.REJECT,
                        });
                      },
                      variant: 'destructive',
                    });
                  }}
                />
              </Can>
            )}
            <Can rule={{ allOf: [PERMS.plan.request.approve] }}>
              <Trash2
                size={16}
                className={`cursor-pointer text-destructive-text hover:text-destructive-text ${status === 'P' && 'hidden'}`}
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirm'),
                    description: t('configurationManagement.plans.page.requestDeleteModel.delete'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    size: 'lg',
                    onAction: async () => {
                      await mutate({
                        clubId: row.original.clubId,
                        planRequestId: row.original.planRequestId ?? '',
                        status: PLAN_REQUEST_STATUS.DELETE,
                      });
                    },
                    variant: 'destructive',
                  });
                }}
              />
            </Can>
          </div>
        );
      },
      size: 40,
    },
  ];
}
